import { useState, useEffect, useCallback, useRef } from 'react';
import { CallState } from '../../types';
import { getCallTraceId } from '../../services/call-log/call-log.service';
import {
  clearAcwPendingContext,
  loadAcwPendingContext,
  saveAcwPendingContext,
} from './acw-pending-context';
import { unlockAcwUi } from './acw-ui-lock';
import { isLiveCallTrackingStatus } from './call-status.util';
import { LastCallInfo } from './types';

function buildCallSnapshot(callState: CallState): LastCallInfo | null {
  const contactId = callState.contactId;
  if (!contactId) {
    return null;
  }

  return {
    contactId,
    skillId: callState.skillId || '',
    skillName: callState.skillName || undefined,
    requireDisposition: callState.requireDisposition,
    callTraceId: getCallTraceId() ?? undefined,
    direction: callState.direction,
    callerName: callState.callerName,
    callerNumber: callState.callerNumber,
    dnis: callState.dnis,
    startTime: callState.startTime,
  };
}

function mergeSnapshot(
  current: LastCallInfo | null,
  next: LastCallInfo,
  options?: { endedAt?: Date; clearEndedAt?: boolean }
): LastCallInfo {
  return {
    ...current,
    ...next,
    callTraceId: next.callTraceId ?? current?.callTraceId,
    callerName: next.callerName ?? current?.callerName ?? null,
    callerNumber: next.callerNumber ?? current?.callerNumber ?? null,
    dnis: next.dnis ?? current?.dnis ?? null,
    startTime: next.startTime ?? current?.startTime ?? null,
    direction: next.direction ?? current?.direction ?? null,
    skillName: next.skillName ?? current?.skillName,
    endedAt: options?.clearEndedAt ? undefined : (options?.endedAt ?? current?.endedAt),
  };
}

export function useCallTracking(callState: CallState, isInAcw = false) {
  const [lastCallInfo, setLastCallInfo] = useState<LastCallInfo | null>(() =>
    loadAcwPendingContext()
  );
  const [lastCallerPhone, setLastCallerPhone] = useState<string | null>(
    () => loadAcwPendingContext()?.callerNumber ?? null
  );
  const prevStatusRef = useRef(callState.status);
  const snapshotRef = useRef<LastCallInfo | null>(null);

  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = callState.status;

    const isLiveCall = isLiveCallTrackingStatus(callState.status);

    if (isLiveCall) {
      const snapshot = buildCallSnapshot(callState);
      if (snapshot) {
        const merged = mergeSnapshot(snapshotRef.current, snapshot, { clearEndedAt: true });
        snapshotRef.current = merged;
        setLastCallInfo(merged);
      } else if (callState.callerNumber || callState.callerName) {
        setLastCallInfo((prev) => {
          if (!prev) {
            return prev;
          }
          const merged = mergeSnapshot(
            prev,
            {
              ...prev,
              callerName: callState.callerName ?? prev.callerName,
              callerNumber: callState.callerNumber ?? prev.callerNumber,
              dnis: callState.dnis ?? prev.dnis,
              startTime: callState.startTime ?? prev.startTime,
              direction: callState.direction ?? prev.direction,
            },
            { clearEndedAt: true }
          );
          snapshotRef.current = merged;
          return merged;
        });
      }

      if (callState.callerNumber) {
        setLastCallerPhone(callState.callerNumber);
      }
      return;
    }

    const callJustEnded =
      isLiveCallTrackingStatus(prevStatus as CallState['status']) &&
      callState.status === 'disconnected';

    if (callJustEnded) {
      const snapshot = buildCallSnapshot({ ...callState, status: prevStatus });
      const base = snapshotRef.current ?? snapshot;
      if (base) {
        const merged = mergeSnapshot(base, snapshot ?? base, { endedAt: new Date() });
        snapshotRef.current = merged;
        setLastCallInfo(merged);
        saveAcwPendingContext(merged);
      }
    }
  }, [callState]);

  useEffect(() => {
    if (!isInAcw || lastCallInfo?.contactId) {
      return;
    }

    const pending = loadAcwPendingContext();
    if (!pending) {
      return;
    }

    snapshotRef.current = pending;
    setLastCallInfo(pending);
    if (pending.callerNumber) {
      setLastCallerPhone(pending.callerNumber);
    }
  }, [isInAcw, lastCallInfo?.contactId]);

  const clearCallTracking = useCallback(() => {
    snapshotRef.current = null;
    setLastCallInfo(null);
    setLastCallerPhone(null);
    clearAcwPendingContext();
    unlockAcwUi();
  }, []);

  return {
    lastCallInfo,
    lastCallerPhone,
    setLastCallerPhone,
    clearCallTracking,
  };
}
